import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { X, Cookie, Settings } from 'lucide-react';

const CONSENT_KEY = 'wheel_cookie_consent';
const CONSENT_VERSION = '1.1'; // Updated version for consent mode

// Helper function to update Google Consent Mode
const updateGoogleConsent = (preferences) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      'ad_storage': preferences.advertising ? 'granted' : 'denied',
      'ad_user_data': preferences.advertising ? 'granted' : 'denied',
      'ad_personalization': preferences.advertising ? 'granted' : 'denied',
      'analytics_storage': preferences.analytics ? 'granted' : 'denied'
    });
  }
};

const CookieBanner = () => {
  const { t } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    advertising: false
  });

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      setShowBanner(true);
      setHasConsented(false);
    } else {
      try {
        const parsed = JSON.parse(consent);
        if (parsed.version !== CONSENT_VERSION) {
          setShowBanner(true);
          setHasConsented(false);
        } else {
          setHasConsented(true);
          setPreferences(parsed.preferences || preferences);
          // Apply saved consent on page load
          updateGoogleConsent(parsed.preferences || preferences);
        }
      } catch {
        setShowBanner(true);
        setHasConsented(false);
      }
    }
  }, []);

  const saveConsent = useCallback((prefs) => {
    const consent = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      preferences: prefs
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
    setShowSettings(false);
    setHasConsented(true);
    setPreferences(prefs);
    
    // Update Google Consent Mode
    updateGoogleConsent(prefs);
  }, []);

  const acceptAll = () => {
    saveConsent({ necessary: true, analytics: true, advertising: true });
  };

  const acceptEssential = () => {
    saveConsent({ necessary: true, analytics: false, advertising: false });
  };

  const saveCustom = () => {
    saveConsent(preferences);
  };

  const reopenBanner = () => {
    setShowBanner(true);
    setShowSettings(true);
  };

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && (
        <div 
          className="cookie-banner"
          role="dialog"
          aria-labelledby="cookie-title"
          aria-describedby="cookie-description"
          aria-modal="true"
          data-testid="cookie-banner"
        >
          <div className="cookie-content">
            <button
              onClick={acceptEssential}
              className="cookie-close"
              aria-label={t('wheel.close')}
              data-testid="cookie-close"
            >
              <X size={20} />
            </button>

            <div className="cookie-header">
              <Cookie size={24} className="cookie-icon" aria-hidden="true" />
              <h2 id="cookie-title">{t('cookies.title')}</h2>
            </div>

            <p id="cookie-description">{t('cookies.description')}</p>

            {showSettings ? (
              <div className="cookie-settings" data-testid="cookie-settings">
                <div className="cookie-option">
                  <label>
                    <input
                      type="checkbox"
                      checked={preferences.necessary}
                      disabled
                    />
                    <span>Necessary (Required)</span>
                  </label>
                </div>
                <div className="cookie-option">
                  <label>
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                    />
                    <span>Analytics</span>
                  </label>
                </div>
                <div className="cookie-option">
                  <label>
                    <input
                      type="checkbox"
                      checked={preferences.advertising}
                      onChange={(e) => setPreferences(p => ({ ...p, advertising: e.target.checked }))}
                    />
                    <span>Advertising</span>
                  </label>
                </div>
                <button
                  onClick={saveCustom}
                  className="btn-cookie btn-primary"
                  data-testid="cookie-save-custom"
                >
                  Save Preferences
                </button>
              </div>
            ) : (
              <div className="cookie-buttons">
                <button
                  onClick={acceptAll}
                  className="btn-cookie btn-primary"
                  data-testid="cookie-accept-all"
                >
                  {t('cookies.acceptAll')}
                </button>
                <button
                  onClick={acceptEssential}
                  className="btn-cookie btn-secondary"
                  data-testid="cookie-essential-only"
                >
                  {t('cookies.essentialOnly')}
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="btn-cookie btn-link"
                  data-testid="cookie-customize"
                >
                  <Settings size={16} />
                  {t('cookies.customize')}
                </button>
              </div>
            )}

            <Link to="/cookies" className="cookie-learn-more">
              {t('cookies.learnMore')}
            </Link>
          </div>
        </div>
      )}

      {/* Persistent Cookie Icon (bottom-left) - shows after consent */}
      {hasConsented && !showBanner && (
        <button
          onClick={reopenBanner}
          className="cookie-icon-btn"
          aria-label="Manage cookie preferences"
          title="Cookie Preferences"
          data-testid="cookie-icon-btn"
        >
          <Cookie size={20} />
        </button>
      )}
    </>
  );
};

export default CookieBanner;
